/** Apple owns authorization UI; Firebase still verifies the ID token and nonce. */
const SDK_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
interface AppleResponse {
  authorization?: { id_token?: string; state?: string };
  user?: { name?: { firstName?: string; lastName?: string } };
}
interface AppleSDK {
  auth: {
    init(config: Record<string, string | boolean>): void;
    signIn(): Promise<AppleResponse>;
  };
}
declare global { interface Window { AppleID?: AppleSDK } }
export interface AppleSignInRequest {
  signIn(): Promise<{ idToken: string; rawNonce: string; displayName?: string }>;
}
const authError = (code: string) => Object.assign(new Error(code), { code });
let sdkLoading: Promise<AppleSDK> | undefined;

function loadSDK(): Promise<AppleSDK> {
  if (window.AppleID) return Promise.resolve(window.AppleID);
  if (sdkLoading) return sdkLoading;
  sdkLoading = new Promise<AppleSDK>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    const fail = () => {
      clearTimeout(timer);
      script.remove();
      reject(authError('auth/network-request-failed'));
    };
    const timer = window.setTimeout(fail, 15000);
    script.onerror = fail;
    script.onload = () => {
      clearTimeout(timer);
      if (window.AppleID) resolve(window.AppleID);
      else fail();
    };
    document.head.appendChild(script);
  }).catch(error => { sdkLoading = undefined; throw error; });
  return sdkLoading;
}

const hex = (bytes: Uint8Array) => Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

/**
 * Origins registered as Return URLs on the Apple service ID
 * (developer.apple.com → Identifiers → Services IDs → com.givethea.web.signin).
 *
 * Popup mode delivers Apple's response by postMessage to the ORIGIN of
 * `redirectURI`, and the SDK resolves only when that origin is the page's own.
 * The former hard-coded apex URL meant every www.givethea.com visitor finished
 * Face ID and got `popup_closed_by_user` — the THE-579 zero-account gap since
 * 2026-05-04. Both hosts serve the site and Hosting cannot redirect by host, so
 * the page must ask Apple to answer wherever it is actually running.
 *
 * An unregistered origin (preview channel, localhost) falls back to apex: it
 * still cannot complete, but fails exactly as before. Register a host in the
 * portal FIRST, then add it here — the portal is the source of truth.
 */
const APPLE_RETURN_ORIGINS: readonly string[] = ['https://givethea.com', 'https://www.givethea.com'];
const APPLE_RETURN_PATH = '/__/auth/handler';

export function appleRedirectURI(origin: string): string {
  const host = APPLE_RETURN_ORIGINS.includes(origin) ? origin : APPLE_RETURN_ORIGINS[0];
  return `${host}${APPLE_RETURN_PATH}`;
}

/** Prepare before the click: no network/digest await may precede opening the popup. */
export async function prepareAppleSignIn(): Promise<AppleSignInRequest> {
  const rawNonce = hex(crypto.getRandomValues(new Uint8Array(32)));
  const state = hex(crypto.getRandomValues(new Uint8Array(32)));
  const [sdk, hash] = await Promise.all([
    loadSDK(), crypto.subtle.digest('SHA-256', new TextEncoder().encode(rawNonce)),
  ]);
  let used = false;
  return {
    async signIn() {
      if (used) throw authError('auth/cancelled-popup-request');
      used = true;
      sdk.auth.init({
        clientId: 'com.givethea.web.signin',
        redirectURI: appleRedirectURI(window.location.origin),
        scope: 'name email', usePopup: true, state, nonce: hex(new Uint8Array(hash)),
      });
      let response: AppleResponse;
      try {
        response = await sdk.auth.signIn();
      } catch (error) {
        const code = (error as { error?: string })?.error;
        if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize') {
          // Apple reports both of these for a genuine "changed my mind" AND for
          // an authorization that completed and then failed on the way back.
          // Callers treat it as a cancel.
          throw authError('auth/popup-closed-by-user');
        }
        if (code === 'popup_blocked_by_browser') throw authError('auth/popup-blocked');
        throw authError('auth/operation-not-allowed');
      }
      const stateOk = response.authorization?.state === state;
      const idToken = response.authorization?.id_token;
      if (!stateOk || !idToken) throw authError('auth/invalid-credential');
      const name = response.user?.name;
      const displayName = [name?.firstName, name?.lastName].filter(Boolean).join(' ').trim();
      return { idToken, rawNonce, displayName: displayName || undefined };
    },
  };
}
